export interface QualityThresholds {
  blurThreshold: number;
  brightnessMin: number;
  brightnessMax: number;
  faceMinPx: number;
  yawThresholdDeg: number;
  pitchThresholdDeg: number;
  livenessDirectionTimeoutMs: number;
}

export type LivenessDirection = 'right' | 'left' | 'up' | 'down';

export type LivenessMode = 'disabled' | 'minimum' | 'strong';
export const DEFAULT_LIVENESS_MODE: LivenessMode = 'disabled';

export const SCAN_QUALITY_THRESHOLDS: QualityThresholds = {
  blurThreshold: 50,
  brightnessMin: 80,
  brightnessMax: 200,
  faceMinPx: 110,
  yawThresholdDeg: 20,
  pitchThresholdDeg: 20,
  livenessDirectionTimeoutMs: 6000,
};

export const QUALITY_GATE_TIMEOUT_MS = 120_000;

/** Pure helper: maps quality report fields to a hint i18n key, or null if all pass. */
export function getQualityHint(
  report: { blur: number; brightness: number; faceSize: number; faceDetected: boolean },
  thresholds: QualityThresholds,
): string | null {
  if (!report.faceDetected) return 'scan.hint.noFace';
  if (report.blur < thresholds.blurThreshold) return 'scan.hint.blur';
  if (report.brightness < thresholds.brightnessMin) return 'scan.hint.darkScene';
  if (report.brightness > thresholds.brightnessMax) return 'scan.hint.brightScene';
  if (report.faceSize < thresholds.faceMinPx) return 'scan.hint.moveCloser';
  return null;
}

/** Pure helper: confirms a liveness direction from yaw/pitch angles.
 *
 * Sign conventions (from vendor/quality.ts estimatePose()):
 *   right: person turns to their right → yawDeg < -yawThresholdDeg
 *   left:  person turns to their left  → yawDeg > +yawThresholdDeg
 *   up:    chin up (head tilts back)   → pitchDeg < -pitchThresholdDeg
 *   down:  chin down (head tilts fwd)  → pitchDeg > +pitchThresholdDeg
 */
export function confirmLivenessDirection(
  direction: LivenessDirection,
  yawDeg: number,
  pitchDeg: number,
  thresholds: QualityThresholds,
): boolean {
  const t = thresholds.yawThresholdDeg;
  const p = thresholds.pitchThresholdDeg;
  switch (direction) {
    case 'right': return yawDeg < -t;
    case 'left': return yawDeg > t;
    case 'up': return pitchDeg < -p;
    case 'down': return pitchDeg > p;
  }
}

/** Returns a random liveness direction. */
export function pickRandomDirection(): LivenessDirection {
  const dirs: LivenessDirection[] = ['right', 'left', 'up', 'down'];
  return dirs[Math.floor(Math.random() * dirs.length)]!;
}
